import { useCallback, useEffect, useMemo, useReducer, useState } from "react";

import get from "lodash/get";
import uniq from "lodash/uniq";
import { useDispatch } from "react-redux";

import { swSettings } from "app/common/services/swSettings";
import {
    ActivationSubscriptions,
    subscriptionsTrackingNames,
} from "app/pages/activation/components/Shared/Subscriptions/types";
import { checkIsValueExistingInEnum } from "app/pages/activation/helpers/checkIsValueExistingInEnum";
import type { SubscriptionStateSchema } from "app/pages/activation/helpers/settings/subscription";
import { initialState, reducerFn, SubscriptionActions } from "app/pages/activation/helpers/settings/subscription";
import { activationTracking } from "app/pages/activation/services/tracking";
import { useAlertsSubscriptionsStatusQuery } from "app/pages/activation-alerts/queries/alerts/useAlertsSubscriptionsStatusQuery";
import { useSubscribeToAlertsMutation } from "app/pages/activation-alerts/queries/alerts/useSubscribeToAlertsMutation";
import { useUnsubscribeFromAlertsMutation } from "app/pages/activation-alerts/queries/alerts/useUnsubscribeFromAlertsMutation";
import type { Granularity } from "app/pages/activation-alerts/types/PlatformAlerts";
import { useDigestSubscribe } from "app/pages/benchmark-tracker";
import { useDigestUnsubscribe } from "app/pages/benchmark-tracker";
import { getGAMetaDataEffect } from "app/pages/benchmark-tracker";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { ActivationComponentAssets } from "../../helpers/ActivationAssets";

import { useNewsAlertsSubscription } from "./useNewsAlertsSubscription";

type UseSubscriptionsSchema = {
    benchmarkId?: string;
    activationId: string;
    isViewerMode: boolean;
};
//TODO Pls @Kostyantyn make this monster smaller
export const useSubscriptions = ({ benchmarkId, isViewerMode, activationId }: UseSubscriptionsSchema) => {
    const isAlertsAvailable: boolean = swSettings.components.Alerts?.isAllowed;
    const isNewsAlertsAvailable = ActivationComponentAssets.isNewsAlertsAvailable;

    const dispatchRedux = useDispatch();
    const track = activationTracking.settings;

    const { data, isLoading } = useAlertsSubscriptionsStatusQuery(activationId, {
        enabled: isAlertsAvailable,
    });

    const [state, dispatch] = useReducer(reducerFn, initialState);
    const [isChanged, setIsChanged] = useState(false);

    const { mutate: unsubscribeFromDigest } = useDigestUnsubscribe(benchmarkId);
    const { mutate: subscribeToDigest, isPending: isSubscribingDigest } = useDigestSubscribe(
        benchmarkId,
        state.granularity[ActivationSubscriptions.BenchmarkDigest].current,
    );

    const { mutate: subscribeToAlerts, isPending: isSubscribingAlerts } = useSubscribeToAlertsMutation(
        activationId,
        state.switch[ActivationSubscriptions.CompetitorsAlerts].current,
    );

    const { mutate: unsubscribeFromAlerts } = useUnsubscribeFromAlertsMutation(activationId);

    const { newsAlertsSubscribe, newsAlertsUnsubscribe, newsAlertsStatus, isLoadingNewsAlertsStatus } =
        useNewsAlertsSubscription(activationId, isNewsAlertsAvailable);

    const setSwitchAction = useCallback(
        (isSubscribed: boolean, subscriptionName: ActivationSubscriptions, isSubscribedDefault?: boolean) => {
            dispatch({
                type: SubscriptionActions.SetSwitch,
                payload: {
                    subscriptionName: subscriptionName,
                    value: {
                        current: isSubscribed,
                        default: isSubscribedDefault || state.switch[subscriptionName].default,
                    },
                },
            });
        },
        [state.switch],
    );

    const setGranularityAction = useCallback(
        (granularity: Granularity, subscriptionName: ActivationSubscriptions, granularityDefault?: Granularity) => {
            dispatch({
                type: SubscriptionActions.SetGranularity,
                payload: {
                    subscriptionName: subscriptionName,
                    value: {
                        current: granularity || GranularityNames.WEEKLY,
                        default: granularityDefault || state.granularity[subscriptionName].default,
                    },
                },
            });
        },
        [state.granularity],
    );

    const getSubsCanBeChanged = useCallback((obj: SubscriptionStateSchema): Array<ActivationSubscriptions> => {
        const switchedSubscriptions: ActivationSubscriptions[] = [];

        const { current: competitorsAlertsCurrent, default: competitorsAlertsDefault } =
            obj.switch[ActivationSubscriptions.CompetitorsAlerts];
        const { current: benchmarkDigestCurrent, default: benchmarkDigestDefault } =
            obj.switch[ActivationSubscriptions.BenchmarkDigest];
        const { current: newsAlertsCurrent, default: newsAlertsDefault } =
            obj.switch[ActivationSubscriptions.NewsAlerts];

        if (competitorsAlertsCurrent || competitorsAlertsCurrent !== competitorsAlertsDefault) {
            switchedSubscriptions.push(ActivationSubscriptions.CompetitorsAlerts);
        }

        if (benchmarkDigestCurrent || benchmarkDigestCurrent !== benchmarkDigestDefault) {
            switchedSubscriptions.push(ActivationSubscriptions.BenchmarkDigest);
        }

        if (newsAlertsCurrent || newsAlertsCurrent !== newsAlertsDefault) {
            switchedSubscriptions.push(ActivationSubscriptions.NewsAlerts);
        }

        return switchedSubscriptions;
    }, []);

    const getChangedSubscriptions = useCallback(
        (state: SubscriptionStateSchema) => {
            const changedSubscriptions: ActivationSubscriptions[] = [];
            let subsName: ActivationSubscriptions | undefined = undefined;

            const getChangedSubscriptionsRecursive = (obj: SubscriptionStateSchema) => {
                if ("default" in obj && "current" in obj) {
                    if (obj.default !== obj.current) {
                        changedSubscriptions.push(subsName);
                    }
                } else {
                    for (const key in obj) {
                        if (checkIsValueExistingInEnum(ActivationSubscriptions, key)) {
                            subsName = key as ActivationSubscriptions;
                        }
                        getChangedSubscriptionsRecursive(obj[key]);
                    }
                }

                return uniq(changedSubscriptions.filter(Boolean));
            };

            const changedSubs = getChangedSubscriptionsRecursive(state);
            const canBeChanged = getSubsCanBeChanged(state);

            return changedSubs.filter((sub) => canBeChanged.includes(sub));
        },
        [getSubsCanBeChanged],
    );

    const isSubscriptionChanged = useCallback(
        (obj: SubscriptionStateSchema) => {
            const changedSubscriptions = getChangedSubscriptions(obj);

            return !!changedSubscriptions.length;
        },
        [getChangedSubscriptions],
    );

    useEffect(() => {
        setIsChanged(isSubscriptionChanged(state));
    }, [state]);

    useEffect(() => {
        if (data) {
            const { granularity } = data;

            setSwitchAction(true, ActivationSubscriptions.CompetitorsAlerts, true);
            setGranularityAction(granularity, ActivationSubscriptions.CompetitorsAlerts, granularity);
        } else {
            setSwitchAction(false, ActivationSubscriptions.CompetitorsAlerts, false);
        }
    }, [data]);

    useEffect(() => {
        if (benchmarkId) {
            const getData = async () => {
                const metaData = await dispatchRedux(getGAMetaDataEffect(benchmarkId));
                const isSubscribed = get(metaData, "digestSubscriptionInfo.isSubscribed");
                const granularity = get(metaData, "digestSubscriptionInfo.granularity");

                setSwitchAction(isSubscribed, ActivationSubscriptions.BenchmarkDigest, isSubscribed);
                setGranularityAction(granularity, ActivationSubscriptions.BenchmarkDigest, granularity);
            };
            getData();
        }
    }, [benchmarkId]);

    useEffect(() => {
        if (!isLoadingNewsAlertsStatus) {
            const isSubscribed = !!newsAlertsStatus;
            const granularity = get(newsAlertsStatus, "granularity", GranularityNames.DAILY);

            setSwitchAction(isSubscribed, ActivationSubscriptions.NewsAlerts, isSubscribed);
            setGranularityAction(granularity, ActivationSubscriptions.NewsAlerts, granularity);
        }
    }, [isLoadingNewsAlertsStatus, newsAlertsStatus]);

    const isSubscribing = isSubscribingAlerts || isSubscribingDigest;

    const subscriptionActions = useMemo(
        () => ({
            [ActivationSubscriptions.BenchmarkDigest]: {
                subscribe: subscribeToDigest,
                unsubscribe: unsubscribeFromDigest,
            },
            [ActivationSubscriptions.CompetitorsAlerts]: {
                subscribe: () =>
                    subscribeToAlerts({
                        granularity: state.granularity[ActivationSubscriptions.CompetitorsAlerts].current,
                    }),
                unsubscribe: unsubscribeFromAlerts,
            },
            [ActivationSubscriptions.NewsAlerts]: {
                subscribe: () =>
                    newsAlertsSubscribe({
                        granularity: state.granularity[ActivationSubscriptions.NewsAlerts].current,
                    }),
                unsubscribe: () => newsAlertsUnsubscribe(),
            },
        }),
        [
            newsAlertsSubscribe,
            newsAlertsUnsubscribe,
            state.granularity,
            subscribeToAlerts,
            subscribeToDigest,
            unsubscribeFromAlerts,
            unsubscribeFromDigest,
        ],
    );

    const onUnsubscribe = useCallback(
        (subscription: ActivationSubscriptions) => subscriptionActions[subscription].unsubscribe(),
        [subscriptionActions],
    );

    const onSubscribe = useCallback(
        (subscription: ActivationSubscriptions) => subscriptionActions[subscription].subscribe(),
        [subscriptionActions],
    );

    const onSave = useCallback(() => {
        getChangedSubscriptions(state).forEach((subscription: ActivationSubscriptions) => {
            if (state.switch[subscription].current) {
                return onSubscribe(subscription);
            }
            return onUnsubscribe(subscription);
        });
    }, [getChangedSubscriptions, onSubscribe, onUnsubscribe, state]);

    const onSwitch = useCallback(
        (value: boolean, subscriptionName: ActivationSubscriptions) => {
            setSwitchAction(value, subscriptionName);
            track.trackSwitchSubscription(value, subscriptionsTrackingNames[subscriptionName], !isViewerMode);
        },
        [setSwitchAction, isViewerMode],
    );

    const onSelect = useCallback(
        (granularity: Granularity, subscriptionName: ActivationSubscriptions) => {
            setGranularityAction(granularity, subscriptionName);
            track.trackChangeSubscriptionGranularity(granularity, subscriptionsTrackingNames[subscriptionName]);
        },
        [setGranularityAction],
    );

    return {
        onSubscribe,
        onUnsubscribe,
        onSwitch,
        onSelect,
        onSave,
        isSwitchedAlert: state.switch[ActivationSubscriptions.CompetitorsAlerts].current,
        isSwitchedNewsAlerts: state.switch[ActivationSubscriptions.NewsAlerts].current,
        isSwitchedDigest: state.switch[ActivationSubscriptions.BenchmarkDigest].current,
        digestGranularity: state.granularity[ActivationSubscriptions.BenchmarkDigest].current,
        alertsGranularity: state.granularity[ActivationSubscriptions.CompetitorsAlerts].current,
        newsAlertsGranularity: state.granularity[ActivationSubscriptions.NewsAlerts].current,
        isLoading,
        isSubscribing,
        isAlertsAvailable,
        isNewsAlertsAvailable,
        isChanged,
    };
};
